import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@tappa/ui';

/**
 * Guida al primo accesso.
 *
 * Non un carosello di schermate promozionali: sei passi che indicano **dove
 * sono le cose** e cosa succede quando le tocchi. Chi apre l'app la prima volta
 * ha davanti un viaggio vuoto e non sa da dove cominciare; questa e' la
 * differenza fra capirlo in trenta secondi e chiudere l'app.
 *
 * Scelte che contano:
 *
 *   * si puo' **uscire in qualsiasi momento**, e uscire vale come "vista": non
 *     ricompare a ogni avvio come un banner che non si riesce a chiudere;
 *   * il passo evidenzia l'elemento vero della barra di navigazione, non un
 *     disegno: cosi' si impara dove tornare;
 *   * si puo' **rivedere** dalle impostazioni del viaggio, perche' la si salta
 *     quasi sempre la prima volta;
 *   * chi ha attivato «riduci le animazioni» non riceve transizioni.
 */

const CHIAVE = 'tappa:guida-vista';

export interface PassoGuida {
  readonly titolo: string;
  readonly testo: string;
  /** Percorso della voce di navigazione da evidenziare. Null = nessuna. */
  readonly rotta: string | null;
}

export const PASSI: readonly PassoGuida[] = [
  {
    titolo: 'Benvenuto',
    testo:
      'Questa app risponde a una domanda sola: cosa faccio adesso. Non è un calendario da riempire — ti dice dove andare, quanto costa, quanto ci metti e quando devi uscire di casa.',
    rotta: null,
  },
  {
    titolo: 'Oggi',
    testo:
      'La schermata da cui parti. In alto la prossima tappa con l’orario in cui conviene muoversi; sotto, la giornata intera. Toccando una tappa si apre il navigatore.',
    rotta: '/oggi',
  },
  {
    titolo: 'Scopri',
    testo:
      'Qui trovi cosa vedere e dove mangiare, ordinato da dove sei. Il pulsante «La mia lista» raccoglie i posti che ti interessano senza deciderne subito il giorno.',
    rotta: '/scopri',
  },
  {
    titolo: 'Organizza le giornate',
    testo:
      'Quando la lista è pronta, un pulsante la distribuisce sui giorni: raggruppa per zona, rispetta orari di apertura e budget, e ti dice cosa non è entrato e perché.',
    rotta: '/scopri',
  },
  {
    titolo: 'Mappa e budget',
    testo:
      'La mappa mostra le tappe numerate nell’ordine in cui le farai. Il budget confronta previsto e speso, giorno per giorno, e segnala prima che tu sfori.',
    rotta: '/mappa',
  },
  {
    titolo: 'Quando un dato non c’è, te lo dico',
    testo:
      'Prezzi e orari vengono da fonti aperte e a volte mancano. In quel caso leggi «non disponibile» o «stima»: non riempio i vuoti con numeri verosimili. Puoi rivedere questa guida da Viaggio.',
    rotta: null,
  },
];

/** Se la guida è già stata vista o chiusa. Un errore di storage non blocca l'app. */
export function guidaGiaVista(): boolean {
  try {
    return window.localStorage.getItem(CHIAVE) === '1';
  } catch {
    // Storage negato (navigazione privata, permessi): si considera vista, così
    // la guida non riappare a ogni avvio senza poter essere chiusa davvero.
    return true;
  }
}

export function segnaGuidaVista(): void {
  try {
    window.localStorage.setItem(CHIAVE, '1');
  } catch {
    // Niente da fare: la guida resterà richiudibile ma non memorizzata.
  }
}

export function riapriGuida(): void {
  try {
    window.localStorage.removeItem(CHIAVE);
  } catch {
    // idem
  }
}

export interface TourProps {
  readonly onChiudi: () => void;
}

export function Tour({ onChiudi }: TourProps): ReactNode {
  const [indice, setIndice] = useState(0);
  const contenitore = useRef<HTMLDivElement>(null);
  const passo = PASSI[indice];

  // Il focus entra nella guida: chi naviga da tastiera o con uno screen reader
  // deve trovarsi dentro, non dietro.
  useEffect(() => {
    contenitore.current?.focus();
  }, []);

  // Evidenzia la voce di navigazione del passo corrente.
  useEffect(() => {
    const rotta = passo?.rotta ?? null;
    if (rotta === null) return undefined;

    const voce = document.querySelector<HTMLElement>(`[data-tour-nav="${rotta}"]`);
    if (voce === null) return undefined;

    voce.setAttribute('data-tour-attiva', 'si');
    return () => {
      voce.removeAttribute('data-tour-attiva');
    };
  }, [passo?.rotta]);

  // Esc chiude, frecce navigano.
  useEffect(() => {
    const suTasto = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onChiudi();
      if (event.key === 'ArrowRight') setIndice((i) => Math.min(i + 1, PASSI.length - 1));
      if (event.key === 'ArrowLeft') setIndice((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', suTasto);
    return () => {
      window.removeEventListener('keydown', suTasto);
    };
  }, [onChiudi]);

  if (passo === undefined) return null;
  const ultimo = indice === PASSI.length - 1;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(0,0,0,0.55)] p-4 pb-[calc(env(safe-area-inset-bottom)+88px)]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guida-titolo"
    >
      <div
        ref={contenitore}
        tabIndex={-1}
        className="w-full max-w-[420px] rounded-[var(--r-lg)] bg-surface p-4 shadow-lg outline-none"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-[13px] font-medium uppercase tracking-wide text-brand-strong">
            Passo {String(indice + 1)} di {String(PASSI.length)}
          </p>
          <button
            type="button"
            onClick={onChiudi}
            aria-label="Chiudi la guida"
            className="-m-2 flex h-11 w-11 items-center justify-center rounded-[var(--r-md)] text-text-muted hover:bg-surface-sunken"
          >
            <X size={20} />
          </button>
        </div>

        <h2 id="guida-titolo" className="mt-1 font-display text-[22px] leading-7 text-text">
          {passo.titolo}
        </h2>
        <p className="mt-2 text-[15px] leading-6 text-text-muted">{passo.testo}</p>

        {/* Puntini: a che punto sei, senza dover leggere il contatore. */}
        <div className="mt-4 flex items-center justify-center gap-1.5" aria-hidden="true">
          {PASSI.map((entry, posizione) => (
            <span
              key={entry.titolo}
              className={
                posizione === indice
                  ? 'h-2 w-5 rounded-[var(--r-full)] bg-brand-strong'
                  : 'h-2 w-2 rounded-[var(--r-full)] bg-border'
              }
            />
          ))}
        </div>

        <div className="mt-4 flex gap-2">
          {indice > 0 ? (
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setIndice(indice - 1);
              }}
            >
              Indietro
            </Button>
          ) : (
            <Button variant="secondary" className="flex-1" onClick={onChiudi}>
              Salta
            </Button>
          )}

          <Button
            className="flex-1"
            onClick={() => {
              if (ultimo) onChiudi();
              else setIndice(indice + 1);
            }}
          >
            {ultimo ? 'Ho capito' : 'Avanti'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
